import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "./components/ui";
import { ArrowRight, Calendar, FileText, LayoutDashboard, Undo2 } from "lucide-react";
import { useLanguage } from "./LanguageContext";

const QUICK_LINKS_CONFIG = [
  {
    key: "invoices",
    to: "/invoices",
    icon: FileText
  },
  {
    key: "returns",
    to: "/returns",
    icon: Undo2
  },
  {
    key: "orders",
    to: "/orders",
    icon: LayoutDashboard
  }
];

const DASHBOARD_TRANSLATIONS = {
  en: {
    heroBadge: "Nestos shop overview",
    heroTitle: "Welcome to your centralized finance & operations cockpit",
    heroSubtitle:
      "Connect your sources, automate invoice downloads and keep an eye on managed returns and orders without leaving the browser.",
    heroHighlight: "Daily snapshot refreshed automatically",
    extensionTitle: "Chrome Extension",
    extensionDescription:
      "Use the Nestos Invoice extension to capture PDFs directly from SellerFlash, SellerRunning and Amazon in one click.",
    extensionButton: "Launch downloader",
    extensionStatusConnected: "Connected",
    extensionStatusDisconnected: "Not connected",
    quickAccessTitle: "Quick access",
    quickAccessCta: "Explore all modules",
    quickLinks: {
      invoices: {
        title: "Invoices",
        description: "Generate, track and export your invoices in one place.",
        cta: "Open Invoices"
      },
      returns: {
        title: "Returns",
        description: "Monitor managed returns across marketplaces. Track them.",
        cta: "Review Returns"
      },
      orders: {
        title: "Orders",
        description: "Stay ahead of shipping deadlines and sync fulfillment.",
        cta: "View Orders",
        badge: "beta"
      }
    }
  },
  tr: {
    heroBadge: "Nestos Shop genel görünüm",
    heroTitle: "Merkezi finans ve operasyon kokpitinize hoş geldiniz",
    heroSubtitle:
      "Kaynaklarınızı bağlayın, fatura indirmelerini otomatikleştirin ve yönetilen iadeler ile siparişleri tarayıcıdan ayrılmadan takip edin.",
    heroHighlight: "Günlük özet otomatik olarak yenilenir",
    extensionTitle: "Chrome Uzantısı",
    extensionDescription:
      "Nestos Invoice uzantısıyla SellerFlash, SellerRunning ve Amazon PDF'lerini tek tıkla yakalayın.",
    extensionButton: "İndiriciyi başlat",
    extensionStatusConnected: "Bağlı",
    extensionStatusDisconnected: "Bağlı değil",
    quickAccessTitle: "Hızlı erişim",
    quickAccessCta: "Tüm modülleri keşfet",
    quickLinks: {
      invoices: {
        title: "Faturalar",
        description: "Faturalarınızı tek noktadan oluşturun, takip edin ve dışa aktarın.",
        cta: "Faturaları aç"
      },
      returns: {
        title: "İadeler",
        description: "Pazar yerlerindeki yönetilen iadeleri izleyin. Takip edin.",
        cta: "İadeleri incele"
      },
      orders: {
        title: "Siparişler",
        description: "Gönderim tarihlerini takip edin ve sevkiyatı senkronize edin.",
        cta: "Siparişleri görüntüle",
        badge: "beta"
      }
    }
  }
};

const Dashboard = () => {
  const [extensionAvailable, setExtensionAvailable] = useState(false);
  const { language } = useLanguage();
  const translations = DASHBOARD_TRANSLATIONS[language] || DASHBOARD_TRANSLATIONS.en;
  const localizedQuickLinks = QUICK_LINKS_CONFIG.map((config) => ({
    ...config,
    ...(translations.quickLinks[config.key] || {})
  }));

  useEffect(() => {
    const handleExtensionReady = () => setExtensionAvailable(true);

    const checkExtension = () => {
      try {
        const attr = document.documentElement?.getAttribute("data-nestos-invoice-extension");
        setExtensionAvailable(attr === "ready");
      } catch {
        setExtensionAvailable(false);
      }
    };

    checkExtension();
    window.addEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
    document.addEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
    const intervalId = setInterval(checkExtension, 5000);

    return () => {
      window.removeEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
      document.removeEventListener("nestosInvoiceExtensionReady", handleExtensionReady);
      clearInterval(intervalId);
    };
  }, []);

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card to-muted/40 px-6 py-10 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-xl space-y-4">
            <Badge variant="secondary" className="uppercase tracking-wide">
              {translations.heroBadge}
            </Badge>
            <h1 className="text-3xl font-semibold leading-tight text-foreground sm:text-4xl">
              {translations.heroTitle}
            </h1>
            <p className="text-sm text-muted-foreground">
              {translations.heroSubtitle}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2 rounded-full bg-background/80 px-3 py-1 font-medium">
                <Calendar className="h-4 w-4 text-primary" />
                {translations.heroHighlight}
              </span>
      </div>
          </div>
          <div className="flex w-full flex-col gap-3 rounded-2xl border border-border/60 bg-background/70 p-5 shadow-sm sm:w-72">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{translations.extensionTitle}</span>
              <Badge
                variant="outline"
                className={
                  extensionAvailable
                    ? "flex items-center gap-1 border-emerald-200 bg-transparent text-emerald-700"
                    : "flex items-center gap-1 border-rose-500/40 bg-transparent text-rose-600"
                }
              >
                <span
                  className={`h-2 w-2 rounded-full ${extensionAvailable ? "bg-emerald-500" : "bg-rose-500"} animate-pulse`}
                />
                {extensionAvailable ? translations.extensionStatusConnected : translations.extensionStatusDisconnected}
              </Badge>
          </div>
            <p className="text-sm text-muted-foreground">
              {translations.extensionDescription}
            </p>
            <Button asChild variant="outline" className="justify-between">
              <Link to="/invoices">
                {translations.extensionButton}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground">{translations.quickAccessTitle}</h2>
          <Button asChild variant="ghost" size="sm" className="gap-2">
            <Link to="/orders">
              {translations.quickAccessCta}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {localizedQuickLinks.map(({ key, title, description, to, icon: Icon, cta, badge }) => (
            <Card key={key} className="group h-full border-border/60 bg-card/70">
              <CardHeader className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
          </div>
                  <CardTitle className="text-xl font-semibold">{title}</CardTitle>
                  {badge && <Badge variant="muted" className="uppercase">{badge}</Badge>}
          </div>
                <CardDescription>{description}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="secondary" className="gap-2">
                  <Link to={to}>
                    {cta}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;